/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  DistanceCalculation,
  FlightRule,
  TransportOfferAlgorithm,
} from 'src/graphql/generated/graphql.generated';
import {
  PreferredUnitRecord,
  PreferredUnitProps,
} from 'src/store/generated/preferred-unit-record.generated';
import {
  IdListItemRecord,
  IdListItemProps,
} from 'src/store/generated/id-list-item-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface OfferAlgorithmProps {
  id: string;
  name: string;
  includeCompetitors: boolean;
  oneCallAgreement: boolean;
  rank: number;
  transportOfferAlgorithm: TransportOfferAlgorithm;
  distanceCalculation: DistanceCalculation;
  activationRuleId: string;
  minimumFlightRule: FlightRule | null;
  companyId: string;
  preferredUnits: List<PreferredUnitProps>;
  companies: List<IdListItemProps> | null;
  isEnabled: boolean;
  createdById: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class OfferAlgorithmRecord extends Record<OfferAlgorithmProps>({
  id: null as any,
  name: null as any,
  includeCompetitors: null as any,
  oneCallAgreement: null as any,
  rank: null as any,
  transportOfferAlgorithm: null as any,
  distanceCalculation: null as any,
  activationRuleId: null as any,
  minimumFlightRule: null as any,
  companyId: null as any,
  preferredUnits: null as any,
  companies: null as any,
  isEnabled: null as any,
  createdById: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    includeCompetitors: boolean;
    oneCallAgreement: boolean;
    rank: number;
    transportOfferAlgorithm: TransportOfferAlgorithm;
    distanceCalculation: DistanceCalculation;
    activationRuleId: string;
    minimumFlightRule: FlightRule | null;
    companyId: string;
    preferredUnits: any;
    companies: any;
    isEnabled: boolean;
    createdById: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): OfferAlgorithmRecord {
    if (json == null) {
      throw new Error('OfferAlgorithmRecord.fromJS: parameter cannot be null');
    }
    return new OfferAlgorithmRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'OfferAlgorithmJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'OfferAlgorithmJson.name'),
      includeCompetitors: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'includeCompetitors', false),
        'OfferAlgorithmJson.includeCompetitors',
      ),
      oneCallAgreement: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'oneCallAgreement', false),
        'OfferAlgorithmJson.oneCallAgreement',
      ),
      rank: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'rank', 0),
        'OfferAlgorithmJson.rank',
      ),
      transportOfferAlgorithm: getFromOrThrow(
        json,
        (j) =>
          propValueOrDefault(
            j,
            'transportOfferAlgorithm',
            TransportOfferAlgorithm.ShortestDistance,
          ),
        'OfferAlgorithmJson.transportOfferAlgorithm',
      ),
      distanceCalculation: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'distanceCalculation', DistanceCalculation.FromAircraft),
        'OfferAlgorithmJson.distanceCalculation',
      ),
      activationRuleId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'activationRuleId', '00000000-0000-0000-0000-000000000000'),
        'OfferAlgorithmJson.activationRuleId',
      ),
      minimumFlightRule: json.minimumFlightRule,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'OfferAlgorithmJson.companyId',
      ),
      preferredUnits: List<PreferredUnitRecord>(
        getFromOrThrow(json, (j) => j.preferredUnits, 'OfferAlgorithmJson.preferredUnits').map(
          (o: any) => new PreferredUnitRecord(o),
        ),
      ),
      companies:
        json.companies != null
          ? List<IdListItemRecord>(json.companies.map((o: any) => new IdListItemRecord(o)))
          : null,
      isEnabled: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isEnabled', false),
        'OfferAlgorithmJson.isEnabled',
      ),
      createdById: json.createdById,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    includeCompetitors: boolean;
    oneCallAgreement: boolean;
    rank: number;
    transportOfferAlgorithm: TransportOfferAlgorithm;
    distanceCalculation: DistanceCalculation;
    activationRuleId: string;
    minimumFlightRule: FlightRule | null;
    companyId: string;
    preferredUnits: any;
    companies: any;
    isEnabled: boolean;
    createdById: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [OfferAlgorithmRecord, Error[]] {
    if (json == null) {
      throw new Error('OfferAlgorithmRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new OfferAlgorithmRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'OfferAlgorithmJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'OfferAlgorithmJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      includeCompetitors: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'includeCompetitors', false),
            'OfferAlgorithmJson.includeCompetitors',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      oneCallAgreement: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'oneCallAgreement', false),
            'OfferAlgorithmJson.oneCallAgreement',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rank: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => propValueOrDefault(j, 'rank', 0), 'OfferAlgorithmJson.rank'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transportOfferAlgorithm: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(
                j,
                'transportOfferAlgorithm',
                TransportOfferAlgorithm.ShortestDistance,
              ),
            'OfferAlgorithmJson.transportOfferAlgorithm',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      distanceCalculation: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'distanceCalculation', DistanceCalculation.FromAircraft),
            'OfferAlgorithmJson.distanceCalculation',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      activationRuleId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'activationRuleId', '00000000-0000-0000-0000-000000000000'),
            'OfferAlgorithmJson.activationRuleId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      minimumFlightRule: tryOrDefault(
        () => json.minimumFlightRule,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'OfferAlgorithmJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      preferredUnits: tryOrDefault(
        () =>
          List<PreferredUnitRecord>(
            getFromOrThrow(json, (j) => j.preferredUnits, 'OfferAlgorithmJson.preferredUnits').map(
              (o: any) => new PreferredUnitRecord(o),
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companies: tryOrDefault(
        () =>
          json.companies != null
            ? List<IdListItemRecord>(json.companies.map((o: any) => new IdListItemRecord(o)))
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isEnabled: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isEnabled', false),
            'OfferAlgorithmJson.isEnabled',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      createdById: tryOrDefault(
        () => json.createdById,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: OfferAlgorithmProps) {
    super(props);
    if (props == null) {
      throw new Error('OfferAlgorithmRecord constructor parameter cannot be null');
    }
  }
}

export abstract class OfferAlgorithmViewModelBase implements OfferAlgorithmProps {
  protected readonly _props: OfferAlgorithmProps;
  private _json?: OfferAlgorithmProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get includeCompetitors(): boolean {
    return this._props.includeCompetitors;
  }
  get oneCallAgreement(): boolean {
    return this._props.oneCallAgreement;
  }
  get rank(): number {
    return this._props.rank;
  }
  get transportOfferAlgorithm(): TransportOfferAlgorithm {
    return this._props.transportOfferAlgorithm;
  }
  get distanceCalculation(): DistanceCalculation {
    return this._props.distanceCalculation;
  }
  get activationRuleId(): string {
    return this._props.activationRuleId;
  }
  get minimumFlightRule(): FlightRule | null {
    return this._props.minimumFlightRule;
  }
  get companyId(): string {
    return this._props.companyId;
  }
  get preferredUnits(): List<PreferredUnitProps> {
    return this._props.preferredUnits;
  }
  get companies(): List<IdListItemProps> | null {
    return this._props.companies;
  }
  get isEnabled(): boolean {
    return this._props.isEnabled;
  }
  get createdById(): string | null {
    return this._props.createdById;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isShortestDistance() {
    return this._props.transportOfferAlgorithm === TransportOfferAlgorithm.ShortestDistance;
  }
  public get isShortestEta() {
    return this._props.transportOfferAlgorithm === TransportOfferAlgorithm.ShortestEta;
  }
  public get isFromAircraft() {
    return this._props.distanceCalculation === DistanceCalculation.FromAircraft;
  }
  public get isFromBase() {
    return this._props.distanceCalculation === DistanceCalculation.FromBase;
  }
  public get isIfr() {
    return this._props.minimumFlightRule === FlightRule.Ifr;
  }
  public get isNone() {
    return this._props.minimumFlightRule === FlightRule.None;
  }
  public get isVfr() {
    return this._props.minimumFlightRule === FlightRule.Vfr;
  }

  public isAnyTransportOfferAlgorithm(...transportOfferAlgorithm: TransportOfferAlgorithm[]) {
    return transportOfferAlgorithm.includes(this._props.transportOfferAlgorithm);
  }
  public isAnyDistanceCalculation(...distanceCalculation: DistanceCalculation[]) {
    return distanceCalculation.includes(this._props.distanceCalculation);
  }
  public isAnyMinimumFlightRule(...flightRule: FlightRule[]) {
    return (
      this._props.minimumFlightRule != null && flightRule.includes(this._props.minimumFlightRule)
    );
  }

  constructor(props: OfferAlgorithmProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        includeCompetitors: this._props.includeCompetitors,
        oneCallAgreement: this._props.oneCallAgreement,
        rank: this._props.rank,
        transportOfferAlgorithm: this._props.transportOfferAlgorithm,
        distanceCalculation: this._props.distanceCalculation,
        activationRuleId: this._props.activationRuleId,
        minimumFlightRule: this._props.minimumFlightRule,
        companyId: this._props.companyId,
        preferredUnits: this._props.preferredUnits,
        companies: this._props.companies,
        isEnabled: this._props.isEnabled,
        createdById: this._props.createdById,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllOfferAlgorithmEntities = (state: AppState) =>
  state.entityStores.offerAlgorithm.entities;

export const selectOfferAlgorithmById = (id: string) => (state: AppState) =>
  state.entityStores.offerAlgorithm.entities.get(id);

export const selectOfferAlgorithmMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.offerAlgorithm.meta.get(id);

export const selectOfferAlgorithmAndMetadataById = (id: string) =>
  createSelector(
    selectOfferAlgorithmById(id),
    selectOfferAlgorithmMetadataById(id),
    (offerAlgorithm, meta) => ({ offerAlgorithm, meta }),
  );

export function selectOfferAlgorithmEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllOfferAlgorithmEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, OfferAlgorithmRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectOfferAlgorithmEntitiesByName(name: string) {
  return createSelector(selectAllOfferAlgorithmEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectOfferAlgorithmEntitiesByIncludeCompetitors(includeCompetitors: boolean) {
  return createSelector(selectAllOfferAlgorithmEntities, (entities) =>
    entities.filter((e) => e.includeCompetitors === includeCompetitors).valueSeq(),
  );
}

export function selectOfferAlgorithmEntitiesByOneCallAgreement(oneCallAgreement: boolean) {
  return createSelector(selectAllOfferAlgorithmEntities, (entities) =>
    entities.filter((e) => e.oneCallAgreement === oneCallAgreement).valueSeq(),
  );
}

export function selectOfferAlgorithmEntitiesByRank(rank: number) {
  return createSelector(selectAllOfferAlgorithmEntities, (entities) =>
    entities.filter((e) => e.rank === rank).valueSeq(),
  );
}

export function selectOfferAlgorithmEntitiesByTransportOfferAlgorithm(
  transportOfferAlgorithm: TransportOfferAlgorithm,
) {
  return createSelector(selectAllOfferAlgorithmEntities, (entities) =>
    entities.filter((e) => e.transportOfferAlgorithm === transportOfferAlgorithm).valueSeq(),
  );
}

export function selectOfferAlgorithmEntitiesByDistanceCalculation(
  distanceCalculation: DistanceCalculation,
) {
  return createSelector(selectAllOfferAlgorithmEntities, (entities) =>
    entities.filter((e) => e.distanceCalculation === distanceCalculation).valueSeq(),
  );
}

export function selectOfferAlgorithmEntitiesByActivationRuleId(activationRuleId: string) {
  return createSelector(selectAllOfferAlgorithmEntities, (entities) =>
    entities.filter((e) => e.activationRuleId === activationRuleId).valueSeq(),
  );
}

export function selectOfferAlgorithmEntitiesByMinimumFlightRule(minimumFlightRule: FlightRule) {
  return createSelector(selectAllOfferAlgorithmEntities, (entities) =>
    entities.filter((e) => e.minimumFlightRule === minimumFlightRule).valueSeq(),
  );
}

export function selectOfferAlgorithmEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllOfferAlgorithmEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}

export function selectOfferAlgorithmEntitiesByIsEnabled(isEnabled: boolean) {
  return createSelector(selectAllOfferAlgorithmEntities, (entities) =>
    entities.filter((e) => e.isEnabled === isEnabled).valueSeq(),
  );
}

export function selectOfferAlgorithmEntitiesByCreatedById(createdById: string) {
  return createSelector(selectAllOfferAlgorithmEntities, (entities) =>
    entities.filter((e) => e.createdById === createdById).valueSeq(),
  );
}
//#endregion Selectors
